import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, FileText, FileSpreadsheet, Eye, FileDown } from "lucide-react";
import api, { apiErrorMsg } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { kriteriaWarna } from "@/lib/utils";
import PrintNilaiGabungan from "@/components/PrintNilaiGabungan";

export default function NilaiGabunganPage() {
  const [periodes, setPeriodes] = useState([]);
  const [kelompokList, setKelompokList] = useState([]);
  const [periodeId, setPeriodeId] = useState("");
  const [kelompokFilter, setKelompokFilter] = useState("");
  const [data, setData] = useState(null);
  const [konstanta, setKonstanta] = useState(null);
  const [view, setView] = useState("ringkasan");
  const [siswaIdx, setSiswaIdx] = useState(0);

  useEffect(() => {
    api.get("/admin/periode").then((r) => setPeriodes(r.data));
    api.get("/admin/kelompok").then((r) => setKelompokList(r.data));
    api.get("/constants").then((r) => setKonstanta(r.data));
  }, []);

  // Filter periode by kelompok
  const filteredPeriodes = kelompokFilter ? periodes.filter((p) => p.kelompok_id === kelompokFilter) : periodes;
  useEffect(() => {
    if (!periodeId) { setData(null); return; }
    api.get(`/admin/periode/${periodeId}/hasil`).then((r) => { setData(r.data); setSiswaIdx(0); }).catch((e) => toast.error(apiErrorMsg(e)));
  }, [periodeId]);

  const printSingle = () => window.print();
  const printAll = () => {
    document.body.classList.add("printing-all");
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("printing-all"), 500);
    }, 100);
  };

  const current = data?.hasil?.[siswaIdx];

  return (
    <div className="p-4 md:p-8">
      <div className="no-print">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Konversi & Gabungan</div>
          <h1 className="font-heading text-3xl font-bold text-navy-900">Nilai Rata-rata Gabungan</h1>
          <p className="text-slate-500 mt-1 text-sm">Gabungan Sosiometri + Konversi Observasi per 23 unsur penilaian.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-auto">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Kelompok</label>
            <select value={kelompokFilter} onChange={(e) => { setKelompokFilter(e.target.value); setPeriodeId(""); }} data-testid="nilai-kelompok-filter" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white w-full sm:w-auto sm:min-w-[220px]">
              <option value="">-- Pilih kelompok dulu --</option>
              {kelompokList.map((k) => <option key={k.id} value={k.id}>{k.nama}{k.pendidikan ? ` — ${k.pendidikan}` : ""}</option>)}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Periode</label>
            <select value={periodeId} onChange={(e) => setPeriodeId(e.target.value)} disabled={!kelompokFilter} data-testid="nilai-periode-select" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white w-full sm:w-auto sm:min-w-[280px] disabled:bg-slate-100 disabled:text-slate-400">
              <option value="">{kelompokFilter ? (filteredPeriodes.length === 0 ? "(belum ada periode untuk kelompok ini)" : "-- Pilih periode --") : "-- Pilih kelompok dulu --"}</option>
              {filteredPeriodes.map((p) => <option key={p.id} value={p.id}>{p.jenis === "mingguan" ? "Minggu" : "Bulan"} ke-{p.nomor} • {p.bulan} {p.tahun}{p.kelompok_nama ? ` • ${p.kelompok_nama}` : ""}</option>)}
            </select>
          </div>
          {data && (
            <>
              <button onClick={() => downloadFile(`/admin/periode/${periodeId}/export-pdf/nilai-gabungan`, "NilaiGabungan_Semua.pdf")} className="bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"><FileDown className="w-4 h-4" /> PDF Semua</button>
              <button onClick={() => downloadFile(`/admin/periode/${periodeId}/export/nilai-gabungan`, "NilaiGabungan.xlsx")} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2" data-testid="export-nilai-xlsx"><FileSpreadsheet className="w-4 h-4" /> Unduh Excel</button>
              <div className="flex gap-1 ml-auto">
                <button onClick={() => setView("ringkasan")} className={`px-3 py-2 text-sm rounded-sm ${view === "ringkasan" ? "bg-navy-900 text-white" : "border border-slate-300 hover:bg-slate-50"}`}>Ringkasan</button>
                <button onClick={() => setView("detail")} className={`px-3 py-2 text-sm rounded-sm ${view === "detail" ? "bg-navy-900 text-white" : "border border-slate-300 hover:bg-slate-50"}`}>Detail per Unsur</button>
                <button onClick={() => setView("preview")} className={`px-3 py-2 text-sm rounded-sm flex items-center gap-1.5 ${view === "preview" ? "bg-brass-500 text-white" : "border border-slate-300 hover:bg-slate-50"}`}><Eye className="w-4 h-4" /> Dokumen Cetak</button>
              </div>
            </>
          )}
        </div>

        {data && view === "preview" && (
          <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-auto">
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Pilih Serdik</label>
              <select value={siswaIdx} onChange={(e) => setSiswaIdx(Number(e.target.value))} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white w-full sm:w-auto sm:min-w-[260px]">
                {data.hasil.map((h, i) => <option key={h.siswa.id} value={i}>{i + 1}. {h.siswa.nama}</option>)}
              </select>
            </div>
            <button onClick={printSingle} className="bg-navy-900 hover:bg-navy-800 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"><Printer className="w-4 h-4" /> Cetak Halaman Ini</button>
            <button onClick={() => downloadFile(`/admin/periode/${periodeId}/export-pdf/nilai-gabungan?siswa_id=${current.siswa.id}`, "NilaiGabungan.pdf")} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2" data-testid="pdf-nilai-single"><FileDown className="w-4 h-4" /> PDF Siswa Ini</button>
            <button onClick={printAll} className="bg-brass-500 hover:bg-brass-600 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> Cetak Semua</button>
          </div>
        )}
      </div>

      {/* RINGKASAN VIEW */}
      {data && view === "ringkasan" && (
        <div className="no-print">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {data.hasil.map((h) => {
              const ns = h.nilai_unsur?.nilai_sikap || 0;
              const na = h.nilai_unsur?.nilai_akhir ?? ns;
              const k = h.nilai_unsur?.kriteria || "—";
              return (
                <div key={h.siswa.id} className="bg-white border border-slate-200 rounded-sm p-4">
                  <div className="font-semibold text-navy-900 truncate text-sm">{h.siswa.nama}</div>
                  <div className="text-xs text-slate-500 font-mono mb-2">{h.siswa.nosis}</div>
                  <div className="text-3xl font-bold font-mono text-navy-900">{na.toFixed(2)}</div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-[10px] uppercase text-slate-500">Nilai Siku / Akhir</div>
                    <span className={`px-2 py-0.5 rounded-sm text-xs font-bold border ${kriteriaWarna(k)}`}>{k}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1 mt-3 text-center">
                    <div><div className="text-[9px] uppercase text-slate-400">M.Roh</div><div className="font-mono font-bold text-sm">{(h.nilai_unsur?.mental_rohani || 0).toFixed(2)}</div></div>
                    <div><div className="text-[9px] uppercase text-slate-400">M.Ide</div><div className="font-mono font-bold text-sm">{(h.nilai_unsur?.mental_ideologi || 0).toFixed(2)}</div></div>
                    <div><div className="text-[9px] uppercase text-slate-400">M.Kej</div><div className="font-mono font-bold text-sm">{(h.nilai_unsur?.mental_kejuangan || 0).toFixed(2)}</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DETAIL VIEW */}
      {data && view === "detail" && (
        <div className="no-print bg-white border border-slate-200 rounded-sm overflow-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-navy-900 text-white">
                <th rowSpan="2" className="p-2 border border-navy-800 text-left min-w-[200px]">UNSUR PENILAIAN</th>
                {data.hasil.map((h) => <th key={h.siswa.id} colSpan="3" className="p-2 border border-navy-800 text-center">{h.siswa.nama}</th>)}
              </tr>
              <tr className="bg-navy-800 text-white">
                {data.hasil.map((h) => (
                  <React.Fragment key={h.siswa.id}>
                    <th className="p-1.5 border border-navy-700 text-[9px] uppercase text-emerald-200">Sosio</th>
                    <th className="p-1.5 border border-navy-700 text-[9px] uppercase text-blue-200">Obs</th>
                    <th className="p-1.5 border border-navy-700 text-[9px] uppercase text-brass-200">Rata</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.unsur_penilaian.map((u, i) => (
                <tr key={u.no} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                  <td className="p-2 border border-slate-200 text-xs">{u.no}. {u.nama}</td>
                  {data.hasil.map((h) => (
                    <React.Fragment key={h.siswa.id}>
                      <td className="p-1 border border-slate-200 text-center font-mono">{(h.sosio_rata_per_unsur?.[i] || 0).toFixed(2)}</td>
                      <td className="p-1 border border-slate-200 text-center font-mono">{(h.konversi_per_unsur?.[i] || 0).toFixed(2)}</td>
                      <td className="p-1 border border-slate-200 text-center font-mono font-bold text-navy-900">{(h.nilai_gabungan_per_unsur?.[i] || 0).toFixed(2)}</td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
              <tr className="bg-brass-100 font-bold">
                <td className="p-2 border border-brass-300">NILAI SIKU / AKHIR</td>
                {data.hasil.map((h) => (
                  <td key={h.siswa.id} colSpan="3" className="p-2 border border-brass-300 text-center font-mono text-navy-900">{(h.nilai_unsur?.nilai_akhir ?? h.nilai_unsur?.nilai_sikap ?? 0).toFixed(2)} <span className="ml-2 text-xs">({h.nilai_unsur?.kriteria})</span></td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* PREVIEW (PRINT DOCUMENT) */}
      {data && view === "preview" && current && konstanta && (
        <>
          <div className="print-single overflow-x-auto">
            <PrintNilaiGabungan
              settings={data.settings}
              siswa={current.siswa}
              periode={data.periode}
              catatan_siku={current.catatan_siku || []}
              sosio_rata_per_unsur={current.sosio_rata_per_unsur || []}
              unsur_penilaian={data.unsur_penilaian}
              kode_to_unsur={konstanta.kode_to_unsur}
              rangking={current.rangking}
              nilai_akhir={current.nilai_unsur?.nilai_akhir}
            />
          </div>
          <div className="print-all-wrap" style={{ display: "none" }}>
            {data.hasil.filter((h) => h.catatan_siku).map((h, i, arr) => (
              <div key={h.siswa.id} style={{ pageBreakAfter: i < arr.length - 1 ? "always" : "auto" }}>
                <PrintNilaiGabungan
                  settings={data.settings}
                  siswa={h.siswa}
                  periode={data.periode}
                  catatan_siku={h.catatan_siku || []}
                  sosio_rata_per_unsur={h.sosio_rata_per_unsur || []}
                  unsur_penilaian={data.unsur_penilaian}
                  kode_to_unsur={konstanta.kode_to_unsur}
                  rangking={h.rangking}
                  nilai_akhir={h.nilai_unsur?.nilai_akhir}
                />
              </div>
            ))}
          </div>
        </>
      )}

      <style>{`
        @page { size: A4 landscape; margin: 8mm 10mm; }
        body.printing-all .print-all-wrap { display: block !important; }
        body.printing-all .print-single { display: none !important; }
      `}</style>
    </div>
  );
}
